import React, { forwardRef, useImperativeHandle } from 'react';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import { MaxRectsPacker } from 'maxrects-packer';
import { DPI, SHEET_WIDTH_IN, SHEET_HEIGHT_IN } from '../../config/dtfConfig';

// Minimal IndexedDB helper for large checkout payloads
async function openCheckoutDb() {
  return await new Promise((resolve, reject) => {
    // Bump version so upgrade runs and creates missing stores
    const req = indexedDB.open('dtfSessionDb', 2);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('sessions')) db.createObjectStore('sessions');
      if (!db.objectStoreNames.contains('checkout')) db.createObjectStore('checkout');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function saveCheckoutPayload(sessionId, payload) {
  const db = await openCheckoutDb();
  return await new Promise((resolve, reject) => {
    const tx = db.transaction('checkout', 'readwrite');
    tx.objectStore('checkout').put(payload, sessionId || 'default');
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
}

// Headless sheet builder for PTC fallback (ensures we send real arranged canvases)
async function buildSheetsHeadlessForPTC(imgList) {
  // Preload images into HTMLImageElement
  await Promise.all(
    imgList.map(img => {
      if (!img.__ptcImageEl) {
        return new Promise(resolve => {
          const el = new window.Image();
          el.crossOrigin = 'anonymous';
          el.onload = () => { img.__ptcImageEl = el; resolve(); };
          el.onerror = () => { console.warn('PTC: failed to load image for headless pack', img); resolve(); };
          el.src = img.url || img.src || img.file;
        });
      }
      return Promise.resolve();
    })
  );
  const PAGE_WIDTH = SHEET_WIDTH_IN * DPI;
  const PAGE_HEIGHT = SHEET_HEIGHT_IN * DPI;
  const GAP = 225;
  // Expand by quantity
  const instances = [];
  imgList.forEach(img => {
    const q = Math.max(1, Number(img.quantity) || 1);
    for (let i = 0; i < q; i++) instances.push(img);
  });
  // Pack
  const rects = instances.map((p, i) => ({
    width: Math.round((Number(p.widthIn) || 0) * DPI),
    height: Math.round((Number(p.heightIn) || 0) * DPI),
    data: { idx: i }
  }));
  const packer = new MaxRectsPacker(PAGE_WIDTH, PAGE_HEIGHT, GAP, { smart: true, pot: false, allowRotation: true });
  packer.addArray(rects);
  // Draw sheets
  const dataUrls = [];
  for (const bin of packer.bins) {
    const c = document.createElement('canvas');
    c.width = PAGE_WIDTH; c.height = PAGE_HEIGHT;
    const x = c.getContext('2d');
    for (const r of bin.rects) {
      const p = instances[r.data.idx];
      const el = p.__ptcImageEl;
      const w = r.width, h = r.height;
      if (!el) continue;
      if (r.rot) {
        x.save(); x.translate(r.x + h, r.y); x.rotate(Math.PI / 2); x.drawImage(el, 0, 0, w, h); x.restore();
      } else {
        x.drawImage(el, r.x, r.y, w, h);
      }
    }
    const maxY = bin.rects.reduce((m, r) => Math.max(m, r.y + r.height), 0);
    const trimmed = document.createElement('canvas');
    trimmed.width = c.width; trimmed.height = maxY;
    trimmed.getContext('2d').drawImage(c, 0, 0, c.width, maxY, 0, 0, c.width, maxY);
    dataUrls.push(trimmed.toDataURL('image/png'));
  }
  return dataUrls;
}

const ProceedToCheckout = forwardRef(function ProceedToCheckout({ images, previewImages, sheetDataUrls, sheetGroups, totalCanvasArea, totalCost, totalImages, pictures, previewMode, forcePicturesOnly = false, renderButton = true }, ref) {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('sessionId');
  // Mirror Checkout’s server-side DPI converter (for logging only)
  const CONVERT_PNG_DPI_URL = 'https://convertpngdpi-7tdimhpb4a-uc.a.run.app';

  // Removed blob conversions: expect pictures to carry base64 data URLs already
  // Basic UA mobile detection (keep in sync with ImageArranger)
  const isMobile = (() => {
    if (typeof navigator === 'undefined') return false;
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  })();

  const hasSheets = Array.isArray(sheetDataUrls) && sheetDataUrls.length > 0;
  const hasPictures = Array.isArray(pictures) && pictures.length > 0;
  const allowPicturesOnly = forcePicturesOnly || isMobile;

  // Allow proceeding when we either have pre-generated sheets or are intentionally skipping them
  const canProceed = hasSheets || (allowPicturesOnly && hasPictures);

  const handleCheckout = async () => {
    if (!canProceed) return false; // Require uploaded sheet URLs before proceeding
    if (!auth.currentUser) {
      const returnUrl = window.location.href;
      navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
      return false;
    }
    // Choose the most accurate canvas images to send to checkout
    let canvasImagesForCheckout = hasSheets
      ? sheetDataUrls
      : (previewMode === 'safe' ? null : previewImages);

    if (!canvasImagesForCheckout || !canvasImagesForCheckout.length) {
      if (allowPicturesOnly) {
        console.warn('PTC: pictures-only flow enabled → skipping headless sheet build.');
        canvasImagesForCheckout = [];
      } else {
        // As a last resort (e.g., safe mode + no sheetDataUrls passed), build real sheets headlessly here
        console.warn('PTC: building sheets headlessly because only placeholders are available.');
        try {
          canvasImagesForCheckout = await buildSheetsHeadlessForPTC(images || []);
        } catch (e) {
          console.error('PTC headless build failed, falling back to previewImages:', e);
          canvasImagesForCheckout = previewImages || [];
        }
      }
    }

    console.log("Total cost:", totalCost);
    console.log("images:", images);
    console.log("previewImages:", previewImages);
    console.log("sheetDataUrls:", sheetDataUrls);
    console.log("totalCanvasArea:", totalCanvasArea);
    console.log("pictures:", pictures);
    console.log("✅ PTC will send canvasImages to Checkout:", {
      from: hasSheets ? 'sheetDataUrls' : (allowPicturesOnly ? 'pictures-only' : (previewMode === 'safe' ? 'headless-build' : 'previewImages')),
      canvasImagesCount: canvasImagesForCheckout ? canvasImagesForCheckout.length : 0,
      sample: canvasImagesForCheckout && canvasImagesForCheckout[0] ? (typeof canvasImagesForCheckout[0] + ' ' + (canvasImagesForCheckout[0].slice ? canvasImagesForCheckout[0].slice(0, 60) : '')) : 'none'
    });

    const totalSquareInches = typeof totalCanvasArea === 'number' ? totalCanvasArea : 0;
    const costPerInch = 0.04;

    // Log whether we will rely on server-side DPI conversion (handled in Checkout)
    console.log('PTC: Server-side DPI converter configured?', Boolean(CONVERT_PNG_DPI_URL), CONVERT_PNG_DPI_URL ? `→ ${CONVERT_PNG_DPI_URL}` : '');
    console.log('PTC: Sending original canvases to Checkout; DPI will be converted server-side in Checkout.');

    // Use pictures as-is (base64 data URLs expected; no blob conversions)
    const portablePictures = (pictures || []);

    const payload = {
      totalImages,
      totalSquareInches,
      costPerInch,
      totalCost,
      canvasImages: canvasImagesForCheckout,
      sheetGroups: hasSheets ? sheetGroups : undefined,
      pictures: portablePictures,
      previewMode,
      images: images.map(img => ({
        src: img.url || img.src,
        name: img.name,
        widthIn: img.widthIn,
        heightIn: img.heightIn,
        quantity: img.quantity,
      })),
    };
    console.log('PTC: saving payload to IDB with', {
      sessionId,
      canvasImagesCount: canvasImagesForCheckout.length,
      from: hasSheets ? 'sheetDataUrls' : (allowPicturesOnly ? 'pictures-only' : (previewMode === 'safe' ? 'headless-build' : 'previewImages'))
    });
    console.log("Storing large checkout payload in IndexedDB (session)", { sessionId, totalImages });
    try {
      await saveCheckoutPayload(sessionId, payload);
      const checkoutPath = sessionId ? `/checkout?sessionId=${encodeURIComponent(sessionId)}` : '/checkout';
      navigate(checkoutPath, { state: { totalCost, costPerInch, sessionId, previewMode } });
    } catch (e) {
      console.warn('Failed to persist checkout payload, falling back to state:', e);
      const checkoutPath = sessionId ? `/checkout?sessionId=${encodeURIComponent(sessionId)}` : '/checkout';
      navigate(checkoutPath, { state: { totalImages, totalSquareInches, costPerInch, totalCost, sessionId, previewMode, canvasImages: canvasImagesForCheckout } });
    }
    return true;
  };

  useImperativeHandle(ref, () => ({ proceed: handleCheckout }));

  if (!renderButton) {
    return null;
  }

  return (
    <Button
      variant="contained"
      color="secondary"
      onClick={() => { handleCheckout(); }}
      disabled={!canProceed}
      style={{
        border: '2.5px solid #A7C667',
        color: canProceed ? '#A7C667' : '#aaa',
        backgroundColor: canProceed ? 'transparent' : '#f2f2f2',
        padding: '0.3rem 2.2rem',
        borderRadius: '10px',
        fontSize: '1.1rem',
        fontWeight: 500,
        marginTop: '3rem',
        cursor: canProceed ? 'pointer' : 'not-allowed',
        fontFamily: 'Anek Latin',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      {canProceed ? 'Proceed To Checkout' : 'Upload at least one picture'}
    </Button>
  );
});

export default ProceedToCheckout;
